import React, { useState } from 'react';
import { useKeycloak } from '@react-keycloak/web';
import { useTranslation } from 'react-i18next';
import { noteApi } from '../utils/api';
import { Card, CardBody, Button, Row, Col, Spinner, Alert } from 'reactstrap';

const AdminPanel = () => {
    const { keycloak } = useKeycloak();
    const { t } = useTranslation();
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState({ reindexES: false, refreshMeta: false, stats: true });
    const [message, setMessage] = useState({ text: '', color: '' });

    const isAdmin = keycloak.hasRealmRole('admin');

    React.useEffect(() => {
        if (isAdmin) {
            fetchStats();
        }
    }, [isAdmin]);

    const fetchStats = async () => {
        try {
            const res = await noteApi.get('stats');
            setStats(res.data);
        } catch (err) {
            console.error("Failed to fetch stats", err);
        } finally {
            setLoading(prev => ({ ...prev, stats: false }));
        }
    };

    if (!isAdmin) {
        return (
            <div className="container mt-5 text-center">
                <Alert color="danger" className="rounded-4 shadow-sm py-4">
                    <h4 className="fw-bold">{t('no_permissions')}</h4>
                    <p className="mb-0">{t('admin_only_page')}</p>
                </Alert>
            </div>
        );
    }

    const handleAction = async (action, type) => {
        setLoading(prev => ({ ...prev, [type]: true }));
        setMessage({ text: '', color: '' });
        try {
            if (action === 'reindexES') {
                await noteApi.post('reindex-all');
                setMessage({ text: t('reindex_es_success'), color: "success" });
            } else if (action === 'refreshMeta') {
                await noteApi.post('attachments/reindex');
                setMessage({ text: t('refresh_meta_success'), color: "success" });
            }
        } catch (err) {
            console.error("Admin action failed", err);
            setMessage({ text: t('admin_action_error'), color: "danger" });
        } finally {
            setLoading(prev => ({ ...prev, [type]: false }));
        }
    };

    return (
        <div className="container mt-4 pb-5 animate-fade-in">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h2 className="fw-bold mb-0">🛡️ {t('admin_panel_title')}</h2>
            </div>

            {message.text && (
                <Alert color={message.color} className="rounded-4 mb-4 border-0 shadow-sm animate-fade-in">
                    {message.text}
                </Alert>
            )}

            <Row className="g-4 mb-4">
                <Col md={12}>
                    <Card className="border-0 shadow-sm rounded-4 bg-warning text-white">
                        <CardBody className="p-4 d-flex align-items-center justify-content-between">
                            <div>
                                <h5 className="text-white-50 mb-1">{t('total_users')}</h5>
                                <h2 className="display-4 fw-bold mb-0">
                                    {loading.stats ? <Spinner size="sm" /> : (stats?.totalUsers || 0)}
                                </h2>
                            </div>
                            <div className="display-1 opacity-25">👥</div>
                        </CardBody>
                    </Card>
                </Col>
            </Row>

            <Row className="g-4">
                <Col md={6}>
                    <Card className="border-0 shadow-sm rounded-4 h-100 bg-card-custom">
                        <CardBody className="p-4 d-flex flex-column">
                            <div className="h3 mb-3">🔍</div>
                            <h4 className="fw-bold mb-2">{t('search_engine_es')}</h4>
                            <p className="text-theme opacity-75 mb-4 flex-grow-1">
                                {t('reindex_es_desc')}
                            </p>
                            <Button 
                                color="primary" 
                                className="rounded-pill fw-bold py-2 shadow-sm"
                                onClick={() => handleAction('reindexES', 'reindexES')}
                                disabled={loading.reindexES}
                            >
                                {loading.reindexES ? <Spinner size="sm" /> : t('run_reindex_es')}
                            </Button>
                        </CardBody>
                    </Card>
                </Col>

                <Col md={6}>
                    <Card className="border-0 shadow-sm rounded-4 h-100 bg-card-custom">
                        <CardBody className="p-4 d-flex flex-column">
                            <div className="h3 mb-3">📄</div>
                            <h4 className="fw-bold mb-2">{t('file_metadata_tika')}</h4>
                            <p className="text-theme opacity-75 mb-4 flex-grow-1">
                                {t('refresh_meta_desc')}
                            </p>
                            <Button 
                                color="info" 
                                className="rounded-pill fw-bold py-2 shadow-sm text-white"
                                onClick={() => handleAction('refreshMeta', 'refreshMeta')}
                                disabled={loading.refreshMeta}
                            >
                                {loading.refreshMeta ? <Spinner size="sm" /> : t('run_refresh_meta')}
                            </Button>
                        </CardBody>
                    </Card>
                </Col>
            </Row>

            <style>{`
                .bg-card-custom { background-color: var(--sb-card-bg)!important; }
                .text-theme { color: var(--sb-text)!important; }
            `}</style>
        </div>
    );
};

export default AdminPanel;
